import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { useBookingChannel } from '../../hooks/useBookingChannel';
import LiveMap from '../LiveMap';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../ui/input-otp';
import { MapPin, Phone, Clock, Navigation, CheckCircle, ArrowLeft, Wifi, KeyRound, Banknote } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import EmergencySOSButton from '../EmergencySOSButton';
import PrivateCallButton from '../PrivateCallButton';
import { BrandLogo } from '../BrandLogo';

const formatTime = (seconds) => {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const ActiveTrip = () => {
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const [showCashModal, setShowCashModal] = useState(false);

  const fetchTrip = useCallback(async () => {
    try {
      const { data } = await api.get('/api/driver/active-trip');
      setTrip(data);
    } catch {
      setTrip(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTrip(); }, [fetchTrip]);

  // Real-time channel — driver-side
  const { sendLocation, connected, lastLocation } = useBookingChannel(trip?.id);

  // Push browser geolocation to the channel every ~5s while a trip is active.
  // Falls back to a small jitter around a default origin if the user denies permission.
  useEffect(() => {
    if (!trip || !['assigned', 'arriving', 'started'].includes(trip.status)) return;
    if (!('geolocation' in navigator)) return;

    let watchId = null;
    let fallbackTimer = null;
    let lastSentAt = 0;

    const push = (lat, lng) => {
      const now = Date.now();
      if (now - lastSentAt < 4000) return; // throttle to ~one update / 4s
      lastSentAt = now;
      sendLocation(lat, lng);
    };

    try {
      watchId = navigator.geolocation.watchPosition(
        (pos) => push(pos.coords.latitude, pos.coords.longitude),
        () => {
          // Permission denied or error — fall back to a slow jitter around Delhi
          let lat = 28.6139;
          let lng = 77.2090;
          fallbackTimer = setInterval(() => {
            lat += (Math.random() - 0.5) * 0.001;
            lng += (Math.random() - 0.5) * 0.001;
            push(lat, lng);
          }, 5000);
        },
        { enableHighAccuracy: true, maximumAge: 4000, timeout: 10000 }
      );
    } catch {
      // ignore
    }

    return () => {
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      if (fallbackTimer) clearInterval(fallbackTimer);
    };
  }, [trip?.id, trip?.status, sendLocation]);

  // Elapsed timer while status === 'started'
  useEffect(() => {
    if (!trip || trip.status !== 'started') return;
    const timer = setInterval(() => setElapsedTime((p) => p + 1), 1000);
    return () => clearInterval(timer);
  }, [trip?.status]);

  // Initialise elapsedTime from started_at if available (basic: from assigned_at as fallback)
  useEffect(() => {
    if (trip?.status === 'started') {
      // No started_at field — use assigned_at as the closest proxy
      const startedSrc = trip?.assigned_at;
      if (startedSrc) {
        const started = new Date(startedSrc).getTime();
        setElapsedTime(Math.max(0, Math.floor((Date.now() - started) / 1000)));
      }
    }
  }, [trip?.id, trip?.status, trip?.assigned_at]);

  const handleStatusUpdate = async () => {
    if (!trip || updating) return;
    const next = {
      assigned: 'arriving',
      arriving: 'started',
      started: 'completed',
    }[trip.status];
    if (!next) return;

    // Starting the trip requires the rider's 4-digit PIN — collect it first.
    if (next === 'started') {
      setPinInput('');
      setPinError('');
      setShowPinModal(true);
      return;
    }

    // Cash bookings need the driver to confirm they actually received the money.
    if (next === 'completed' && trip.payment_method === 'cash') {
      setShowCashModal(true);
      return;
    }

    setUpdating(true);
    try {
      const { data } = await api.patch(`/api/driver/bookings/${trip.id}/status`, { status: next });
      const messages = {
        arriving: { title: 'Status Updated', description: 'Marked as arriving' },
        completed: { title: 'Trip Completed!', description: 'Earnings added to your account' },
      };
      toast(messages[next]);
      if (next === 'completed') {
        setCompleted(true);
        setTimeout(() => navigate('/driver/dashboard'), 2500);
      } else {
        setTrip(data);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({
        title: 'Update failed',
        description: typeof detail === 'string' ? detail : 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirmCashReceived = async () => {
    if (!trip || updating) return;
    setUpdating(true);
    try {
      await api.patch(`/api/driver/bookings/${trip.id}/status`, { status: 'completed', cash_received: true });
      toast({ title: 'Trip Completed!', description: 'Cash payment confirmed — earnings added to your account' });
      setShowCashModal(false);
      setCompleted(true);
      setTimeout(() => navigate('/driver/dashboard'), 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({
        title: 'Update failed',
        description: typeof detail === 'string' ? detail : 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleConfirmPin = async () => {
    if (!trip || pinInput.length !== 4) return;
    setUpdating(true);
    setPinError('');
    try {
      const { data } = await api.patch(`/api/driver/bookings/${trip.id}/status`, { status: 'started', pin: pinInput });
      toast({ title: 'Trip Started', description: 'Timer has begun' });
      setTrip(data);
      setShowPinModal(false);
      setPinInput('');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setPinError(typeof detail === 'string' ? detail : 'Incorrect PIN. Please try again.');
      setPinInput('');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading trip…</div>;
  }

  if (!trip && !completed) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-6">
        <p className="text-muted-foreground">No active trip right now.</p>
        <Button onClick={() => navigate('/driver/dashboard')}>Back to Dashboard</Button>
      </div>
    );
  }

  const statusButtons = {
    assigned: { text: 'Start Navigation', icon: <Navigation className="w-5 h-5" /> },
    arriving: { text: 'Start Trip', icon: <Clock className="w-5 h-5" /> },
    started: { text: 'Complete Trip', icon: <CheckCircle className="w-5 h-5" /> },
  };
  const btn = trip ? statusButtons[trip.status] : null;

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b p-4">
        <div className="container mx-auto">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/driver/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <BrandLogo variant="compact" className="h-7 w-auto" />
            <h1 className="text-2xl font-bold border-l pl-4">Active Trip</h1>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-2xl">
        <Card className={`p-6 mb-6 ${trip?.status === 'started' ? 'bg-gradient-to-r from-green-600 to-green-500' : 'bg-gradient-to-r from-primary to-primary-dark'} text-white`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold mb-2" data-testid="trip-status-banner">
                {completed && 'Trip Completed'}
                {trip?.status === 'assigned' && 'Trip Assigned'}
                {trip?.status === 'arriving' && 'On The Way'}
                {trip?.status === 'started' && 'Trip in Progress'}
              </h2>
              {trip?.status === 'started' && (
                <div className="text-4xl font-bold font-mono">{formatTime(elapsedTime)}</div>
              )}
            </div>
            {trip && !completed && (
              <div className="flex items-center gap-1 bg-white/15 backdrop-blur px-3 py-1.5 rounded-full text-sm">
                <Wifi className={`w-4 h-4 ${connected ? 'text-green-200' : 'text-white/50'}`} />
                {connected ? 'Sharing live location' : 'Connecting…'}
              </div>
            )}
          </div>
        </Card>

        <Card className="mb-6 overflow-hidden">
          {lastLocation ? (
            <LiveMap lat={lastLocation.lat} lng={lastLocation.lng} height={256} />
          ) : (
            <div className="relative h-64 bg-gradient-to-br from-muted to-background flex items-center justify-center">
              <div className="text-center">
                <MapPin className="w-12 h-12 mx-auto mb-2 text-muted-foreground" />
                <p className="text-muted-foreground">Live Navigation</p>
              </div>
            </div>
          )}
        </Card>

        {trip && (
          <>
            <Card className="p-6 mb-6">
              <h3 className="font-semibold text-lg mb-4">Customer Details</h3>
              <div className="flex items-center gap-4 mb-4">
                <Avatar className="w-16 h-16">
                  <AvatarImage src="https://i.pravatar.cc/150?img=1" />
                  <AvatarFallback>U</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <h4 className="font-semibold text-lg">Customer</h4>
                  <p className="text-sm text-muted-foreground flex items-center gap-2">
                    <Phone className="w-4 h-4" />{trip.customer_phone || 'Number unavailable'}
                  </p>
                </div>
                <PrivateCallButton contactName="Customer" contactNumber={trip.customer_phone} userType="driver" />
              </div>
            </Card>

            <div className="mb-6">
              <EmergencySOSButton userType="driver" />
            </div>

            <Card className="p-6 mb-6">
              <h3 className="font-semibold text-lg mb-4">Trip Details</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Pickup Location</p>
                    <p className="font-medium">{trip.pickup_location}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Drop Location</p>
                    <p className="font-medium">{trip.drop_location}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Duration</p>
                    <p className="font-medium">{trip.hours} Hour(s)</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">₹</span>
                  <div>
                    <p className="text-sm text-muted-foreground">Earning</p>
                    <p className="font-bold text-2xl text-green-600">₹{trip.amount}</p>
                  </div>
                </div>
              </div>
            </Card>
          </>
        )}

        {!completed && btn && (
          <Button
            size="lg"
            className="w-full bg-primary hover:bg-primary-dark text-lg"
            onClick={handleStatusUpdate}
            disabled={updating}
            data-testid="trip-status-action-btn"
          >
            {btn.icon}<span className="ml-2">{updating ? 'Updating…' : btn.text}</span>
          </Button>
        )}

        {completed && (
          <Card className="p-6 bg-green-50 border-green-200 text-center">
            <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-600" />
            <h3 className="text-2xl font-bold text-green-900 mb-2">Trip Completed!</h3>
            <p className="text-green-700">Earnings have been added to your account</p>
          </Card>
        )}
      </div>

      {/* PIN verification — ask the rider for their 4-digit trip PIN before starting */}
      <Dialog open={showPinModal} onOpenChange={(open) => { if (!updating) setShowPinModal(open); }}>
        <DialogContent data-testid="pin-verification-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5" />Enter Trip PIN
            </DialogTitle>
            <DialogDescription>
              Ask your customer for the 4-digit PIN shown on their screen to start the trip.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            <InputOTP
              maxLength={4}
              value={pinInput}
              onChange={(v) => { setPinInput(v); setPinError(''); }}
              data-testid="pin-input"
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} className="w-14 h-14 text-2xl" />
                <InputOTPSlot index={1} className="w-14 h-14 text-2xl" />
                <InputOTPSlot index={2} className="w-14 h-14 text-2xl" />
                <InputOTPSlot index={3} className="w-14 h-14 text-2xl" />
              </InputOTPGroup>
            </InputOTP>
            {pinError && (
              <p className="text-sm text-red-600" data-testid="pin-error-message">{pinError}</p>
            )}
            <Button
              className="w-full bg-primary hover:bg-primary-dark"
              disabled={pinInput.length !== 4 || updating}
              onClick={handleConfirmPin}
              data-testid="pin-confirm-btn"
            >
              {updating ? 'Verifying…' : 'Start Trip'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cash confirmation — required before a cash booking can be marked completed */}
      <Dialog open={showCashModal} onOpenChange={setShowCashModal}>
        <DialogContent data-testid="cash-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote className="w-5 h-5 text-green-600" />Confirm Cash Received
            </DialogTitle>
            <DialogDescription>
              This rider chose to pay by cash. Please confirm you have collected ₹{trip?.amount} before completing the trip.
            </DialogDescription>
          </DialogHeader>
          <Button
            className="w-full bg-green-600 hover:bg-green-700"
            disabled={updating}
            onClick={handleConfirmCashReceived}
            data-testid="confirm-cash-received-btn"
          >
            {updating ? 'Completing…' : `Yes, I Received ₹${trip?.amount} Cash`}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActiveTrip;
